import React, { useEffect, useMemo, useRef } from "react";
import { JOURNEY_STATUS_LABELS } from "../../../constants/tspJourneyStatus";
import { formatDate } from "../../../utils/tspFormat";
import { groupPlacesByYear } from "../coverageMapModel";

// The left panel of the coverage map: every JOURNEY PLACE the map holds
// (already narrowed by the Journeys filters) — one row per place of a journey,
// "<Taluka / Town> - <Temple>" with the place's own date — grouped by year,
// newest first. Clicking a place selects it: the map shows it and zooms to it.
// Clicking the selected place again, or "All Places" in the header, clears it
// (`onShowAll`). Only the list scrolls (_coverage-map.scss), and the selected
// place is scrolled into view inside it — never the page.
export default function CoveragePlaceList({ places, placeId, onSelect, onShowAll, filtered = false }) {
  const listRef = useRef(null);
  const groups = useMemo(() => groupPlacesByYear(places), [places]);

  useEffect(() => {
    const list = listRef.current;
    if (!list || !placeId) return;
    const item = list.querySelector(`[data-place-id="${Number(placeId)}"]`);
    if (!item) return;
    const top = item.offsetTop - list.offsetTop;
    if (top < list.scrollTop || top + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = Math.max(0, top - list.clientHeight / 2 + item.offsetHeight / 2);
    }
  }, [placeId, groups]);

  return (
    <section className="tsp-cov-journeys" aria-label="Journey places on the map">
      <h4 className="tsp-cov-journeys-title">
        Journey Places
        <span className="tsp-cov-journeys-head-side">
          {placeId && onShowAll && (
            <button type="button" className="tsp-cov-journeys-all-btn" onClick={onShowAll}>
              All Places
            </button>
          )}
          <span className="tsp-cov-journeys-count">{places.length}</span>
        </span>
      </h4>
      {places.length === 0 ? (
        <div className="tsp-cov-route-message">{filtered ? "No journey place matches these filters." : "No journey places yet."}</div>
      ) : (
        <div className="tsp-cov-journeys-scroll" ref={listRef} data-testid="cov-place-list">
          {groups.map((group) => (
            <div key={group.year ?? "undated"} className="tsp-cov-journeys-year" role="group" aria-label={group.year ? String(group.year) : "No date"}>
              <h5 className="tsp-cov-journeys-year-title">{group.year ?? "No date"}</h5>
              <ul className="tsp-cov-journeys-list">
                {group.places.map((place) => {
                  const selected = place.place_id === placeId;
                  const status = String(place.journey_status || "").toLowerCase();
                  const onMap = Number.isFinite(place.latitude) && Number.isFinite(place.longitude);
                  return (
                    <li key={place.place_id} data-place-id={place.place_id}>
                      <button
                        type="button"
                        className={`tsp-cov-journey tsp-cov-journey--${status}${selected ? " is-selected" : ""}`}
                        aria-pressed={selected}
                        title={selected ? "Click again to show all places" : onMap ? undefined : "No map location saved for this place yet"}
                        onClick={() => (selected && onShowAll ? onShowAll() : onSelect(place.place_id))}
                      >
                        <span className="tsp-cov-journey-dot" aria-hidden="true" />
                        <span className="tsp-cov-journey-text">
                          <span className="tsp-cov-journey-name">{place.place_label}</span>
                          <span className="tsp-cov-journey-sub">
                            {place.place_date ? formatDate(place.place_date) : "No date"}
                            {" · "}
                            <span className="tsp-cov-journey-status">{JOURNEY_STATUS_LABELS[place.journey_status] || place.journey_status}</span>
                            {!onMap && " · not on the map"}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
